import api from "@/axios";

export const manualService = {
  /**
   * Lista manuais sem filtros
   * @param {Object} params - Parâmetros de paginação
   * @returns {Promise} Response da API
   */
  async listar(params = { offset: 0, limit: 10 }) {
    const response = await api.get("/manuals/list", {
      params: { offset: params.offset, limit: params.limit },
    });
    return response;
  },

  /**
   * Lista manuais com filtros
   * @param {Object} filtros - Filtros a serem aplicados (incluindo offset e limit)
   * @returns {Promise} Response da API
   */
  async filtrar(filtros) {
    // Construir o objeto filter conforme esperado pela API
    const filter = {
      offset: 0,
      limit: 20,
      ...filtros,
    };

    const response = await api.get("/manuals/filter", {
      params: { filter: JSON.stringify(filter) },
    });
    return response;
  },

  /**
   * Busca manual por ID
   * @param {number} id - ID do manual
   * @returns {Promise} Response da API
   */
  async buscarPorId(id) {
    try {
      // Tentar API real primeiro
      try {
        const response = await api.get(`/manuals/find/${id}`);

        if (!response.data) {
          throw new Error("Response.data é undefined ou null");
        }

        return response;
      } catch (apiError) {
        console.warn("API falhou, usando mock temporário:", apiError.message);

        // Fallback para mock se API falhar
        await new Promise((resolve) => setTimeout(resolve, 800));

        const mockData = [
          {
            id: 1,
            productId: 1,
            title: "Manual de Instalação - Produto 101",
            description:
              "Guia completo para instalação e configuração do produto.",
            countryVisibility: ["BR", "US", "PT"],
            topics: [
              {
                id: 1,
                manualId: 1,
                title: "1. Requisitos do Sistema",
                subtitle: "Requisitos principais",
                content: `<p>Antes de iniciar a instalação, verifique se seu sistema atende aos requisitos mínimos:</p>
                         <ul><li>Sistema operacional compatível</li><li>Espaço em disco suficiente</li><li>Conexão com a internet</li></ul>`,
                mobilePictureUrl: "images/manual/mobile/requisitos.jpg",
                desktopPictureUrl: "images/manual/desktop/requisitos.jpg",
                order: 1,
                disabled: false,
              },
              {
                id: 2,
                manualId: 1,
                title: "2. Processo de Instalação",
                content: `<p>Siga os passos abaixo para instalar o produto:</p><ol><li>Baixe o instalador</li>
                         <li>Execute o arquivo</li><li>Siga as instruções do assistente</li></ol>`,
                order: 2,
                disabled: false,
              },
            ],
            footerTitle: "Informações adicionais",
            footerContent:
              "Em caso de dúvidas, entre em contato com o suporte técnico.",
            disabled: false,
          },
        ];

        return {
          data: mockData,
          status: 200,
        };
      }
    } catch (error) {
      console.error("Erro ao buscar produto:", error.message);
      throw error;
    }
  },

  /**
   * Busca manuais por ID do produto com suporte a paginação
   * @param {number} idProduto - ID do produto
   * @param {number} [offset] - Offset para paginação
   * @param {number} [limit] - Limite de itens por página
   * @returns {Promise} Response da API
   */
  async buscarPorIdProduto(idProduto, offset = 0, limit = 10) {
    try {
      const response = await api.get(`/products/${idProduto}/manuals`, {
        params: {
          offset,
          limit,
        },
      });

      if (!response.data) {
        throw new Error("Response.data é undefined ou null");
      }

      return response;
    } catch (apiError) {
      console.warn("API falhou, usando mock temporário:", apiError.message);

      // Fallback para mock se API falhar
      await new Promise((resolve) => setTimeout(resolve, 800));

      const mockData = [
        {
          id: 1,
          productId: 1,
          title: "Manual de Instalação - Produto 101",
          description:
            "Guia completo para instalação e configuração do produto.",
          countryVisibility: ["BR", "US", "PT"],
          topics: [
            {
              id: 1,
              manualId: 1,
              title: "1. Requisitos do Sistema",
              subtitle: "Requisitos principais",
              content: `<p>Antes de iniciar a instalação, verifique se seu sistema atende aos requisitos mínimos:</p>
                         <ul><li>Sistema operacional compatível</li><li>Espaço em disco suficiente</li><li>Conexão com a internet</li></ul>`,
              mobilePictureUrl: "images/manual/mobile/requisitos.jpg",
              desktopPictureUrl: "images/manual/desktop/requisitos.jpg",
              order: 1,
              disabled: false,
            },
            {
              id: 2,
              manualId: 1,
              title: "2. Processo de Instalação",
              content: `<p>Siga os passos abaixo para instalar o produto:</p><ol><li>Baixe o instalador</li>
                         <li>Execute o arquivo</li><li>Siga as instruções do assistente</li></ol>`,
              order: 2,
              disabled: false,
            },
          ],
          footerTitle: "Informações adicionais",
          footerContent:
            "Em caso de dúvidas, entre em contato com o suporte técnico.",
          disabled: false,
        },
      ];
      return {
        data: [mockData], // agora retorna uma lista de manuais
        status: 200,
      };
    }
  },

  /**
   * Cria um novo manual para um produto
   * @param {FormData} dataManual - Dados do manual + arquivos
   * @returns {Promise} Response da API
   */
  async criarManual(dataManual) {
    try {
      return await api.post("/products/manuals/create", dataManual, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } catch (error) {
      console.error("Erro ao criar manual:", error);
      throw error;
    }
  },

  /**
   * Atualiza um manual existente
   * @param {number} idManual - ID do manual a ser atualizado
   * @param {FormData} dataManual - Dados do manual + arquivos
   * @returns {Promise} Response da API
   */
  async atualizarManual(idManual, dataManual) {
    try {
      return await api.put(`/products/manuals/update/${idManual}`, dataManual, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    } catch (error) {
      console.error(`Erro ao atualizar manual com ID ${idManual}:`, error);
      throw error;
    }
  },
};

export default manualService;
